import * as angular from 'angular';
import Selection, {SelectionEvent} from '../../../model/model-selection';
import contractService from '../../../service/contract/contract-services';
import security from '../../../service/security/security-services';
import {AuthEvents} from '../../../service/security/security-module';
import {ContractFile, FileNeedsOperation} from '../../../integration/representation/file';
import {ValidationIssue} from '../../../integration/representation/validation-issue';
import datafangstHelpers from "../../../utilities";
import {Operation} from "../contract-features-3/domain";

interface ContractFilesControllersState {
    // Exposed vars
    contract: contractService;
    selectedFile: ContractFile;
    selectedFiles: ContractFile[];
    fileSelection: Selection<string>;
    view: string;
    errorView: 'ALL' | 'ERRORS' |  'ERRORS-UNIQUE-ALIAS';
    orderByField: string;
    reverseSort: boolean;
    canUploadFiles: boolean;
    canDeleteFiles: boolean;
    MAX_FILESIZE_FOR_DISPLAY: number;
    allOperations: Operation | null;

    filesNeedingOperation: Record<string, FileNeedsOperation>;

    // Exposed functions
    deleteSelected: Function;
    downloadSelected: Function;
    printElement: Function;
    updateFileDestination: Function;
    getNrOfFeatureRejectionIssues: Function;
    getNrOfRejectedFeatureWithUniqueAlias: Function;
    getIssueList: Function;
    getNrOfFeatures: Function;
    anyLargeFilesSelected: Function;
    tooManyFeaturesImportedSelected: () => boolean;
    hasRejectedFeature: Function;
    operationToDomain: (operation: string) => void;
    operationFromDomain: (domainOperation: string) => void;

    anySelectedNeedsOperation: () => boolean;
    onAllOperationsChange: (operation: Operation) => void;
    onOperationChange: (id: string, operation: string) => void;
    onConfirmSelectedOperations: () => void;
    onConfirmOperation: (id: string) => void;
}

export default function ContractFilesController($q: angular.IQService,
                                                $uibModal: angular.ui.bootstrap.IModalService,
                                                $scope: any,
                                                $rootScope: angular.IRootScopeService,
                                                AUTH_EVENTS: AuthEvents,
                                                $routeParams: angular.route.IRouteParamsService,
                                                contract: contractService,
                                                security: security) {
    const vm: ContractFilesControllersState = this;

    // Exposed vars
    vm.contract = contract;
    vm.selectedFile = undefined;
    vm.selectedFiles = [];
    vm.fileSelection = new Selection<string>(onSelectionChange);
    vm.view = 'OVERVIEW';
    vm.errorView = 'ALL'; // {ALL, ERRORS, ERRORS-UNIQUE-ALIAS}
    vm.orderByField = 'location.lineNo';
    vm.reverseSort = false;
    vm.canUploadFiles = false;
    vm.canDeleteFiles = false;
    vm.MAX_FILESIZE_FOR_DISPLAY = 1024 * 1024;
    vm.filesNeedingOperation = {};
    vm.allOperations = null;

    // Exposed functions
    vm.deleteSelected = deleteSelected;
    vm.downloadSelected = downloadSelectedFiles;
    vm.printElement = printElement;
    vm.updateFileDestination = updateFileDestination;
    vm.getNrOfFeatureRejectionIssues = getNrOfFeatureRejectionIssues;
    vm.getNrOfRejectedFeatureWithUniqueAlias = getNrOfRejectedFeatureWithUniqueAlias;
    vm.getIssueList = getIssueList;
    vm.getNrOfFeatures = getNrOfFeatures;
    vm.anyLargeFilesSelected = anyLargeFilesSelected;
    vm.hasRejectedFeature = hasRejectedFeature;
    vm.operationToDomain = datafangstHelpers.operationToDomain;
    vm.operationFromDomain = datafangstHelpers.operationFromDomain;
    vm.onAllOperationsChange = onAllOperationsChange;
    vm.onOperationChange = onSelectedOperationChange;
    vm.onConfirmOperation = confirmSelectedOperation;
    vm.onConfirmSelectedOperations = confirmAllSelected;
    vm.anySelectedNeedsOperation = anySelectedNeedsOperation;
    vm.tooManyFeaturesImportedSelected = tooManyFeaturesImportedSelected;

    $scope.registerNewFile = (file: File) => contract.uploadFile(file, (id: string, wantedOperation: Operation | null) => {
        vm.allOperations = null;
        vm.fileSelection.doselect(id);
        vm.filesNeedingOperation[id] = {
            loading: false,
            wantedOperation
        };
    });



    init();

    function init() {
        $q.all([security.ensureCurrentUser(), contract.selectContract($routeParams.contractId)])
            .then((data) => {
                const user = data[0];
                vm.canUploadFiles = user.hasPermission('FILE:CREATE', $routeParams.contractId);
                vm.canDeleteFiles = user.hasPermission('FILE:DELETE', $routeParams.contractId);

                return contract.readFileList();
            })
            .then(() => {
                const filesWithOperation = contract.fileList.files
                    ?.filter(file => file.wantedOperation) ?? [];

                reselectAfterInitOrSubmit(filesWithOperation);
            })
            .catch((error) => {
                console.error("Error during initialization:", error);
            });

        $scope.$on(AUTH_EVENTS.AUTH_LOGGED_OUT, function() {
            clear();
        });
    }

    function anySelectedNeedsOperation() {
        return vm.selectedFiles.some(file => file.wantedOperation);
    }

    function onAllOperationsChange(operation: Operation) {
        if(operation){
            vm.allOperations = operation;
            vm.filesNeedingOperation = Object.entries(vm.filesNeedingOperation)
                .reduce((prev, [id, op]) => ({
                    ...prev,
                    [id]: {
                        loading: op.loading,
                        wantedOperation: vm.selectedFiles.some(selectedFile => selectedFile.id === id) ? operation : op.wantedOperation
                    }
                }), {});
        }
    }

    function reselectAfterInitOrSubmit(files: ContractFile[], removeOperationForId?: string) {
        vm.fileSelection.clear();
        vm.fileSelection.select(files.map(file => file.id), false);
        vm.filesNeedingOperation = files
            .reduce((prev, curr) => ({...prev, [curr.id]: {
                    loading: false,
                    wantedOperation: curr.id === removeOperationForId ? null : curr.wantedOperation
                }}), {});
    }

    function onSelectedOperationChange(id: string, operation: Operation) {
        vm.filesNeedingOperation[id] = {
            loading: false,
            wantedOperation: operation
        };
    }

    function confirmAllSelected() {
        vm.selectedFiles.map(file => file.id)
            .forEach(confirmSelectedOperation);
        vm.allOperations = null;
    }

    function confirmSelectedOperation(id: string) {
        const existing = vm.filesNeedingOperation[id];
        contract.loading.files = true;
        const selectedOp = existing ? existing.wantedOperation : contract.fileList.getFile(id)?.wantedOperation;
        if(selectedOp){
            if(existing){
                vm.filesNeedingOperation[id].loading = true;
            }
            contract.confirmFileOperation(id, selectedOp, () => {
                const withWantedOperation = (contract.fileList.files ?? []).filter(file => file.wantedOperation && file.id !== id);
                reselectAfterInitOrSubmit(withWantedOperation.length > 0 ? withWantedOperation : vm.selectedFiles, id);
            });
        } else {
            contract.showMessage.warning("Kunne ikke bekrefte operasjonstype", "Prøv å velge riktig på nytt");
        }
    }

    function hasRejectedFeature(file: ContractFile): boolean {
        return file.issues.some(issue => issue.location.belongsToRejectedFeature);
    }

    function tooManyFeaturesImportedSelected() {
        return vm.selectedFiles.some(file => file.issues.some(issue => issue.message === "For mange objekter importert, maksimalt 3.000 er tillatt om gangen"));
    }

    function anyLargeFilesSelected() {
        return vm.selectedFiles.some(file => {
                return file.size >= vm.MAX_FILESIZE_FOR_DISPLAY;
            });
    }

    function deleteSelected() {
        contract.deleteFiles(vm.fileSelection.getSelected(), function(fileId) {
            vm.fileSelection.unselect(fileId);
        });
    }

    function downloadSelectedFiles() {
        const files = contract.fileList.getFiles(vm.fileSelection.getSelected());
        contract.downloadFiles(files);
    }

    function printElement(elementId: string) {
        const elemToPrint = document.getElementById(elementId);

        const data = elemToPrint.outerHTML;
        let style = '';
        for (let i = 0; i < document.styleSheets.length; i++) {
            const styleSheet = document.styleSheets[i];
            if (styleSheet.href) {
                style += '<link rel="stylesheet" type="text/css" href="' + styleSheet.href + '"/>';
            }
        }
        const mywindow = window.open('', 'Datafangst', 'height=400,width=1200');
        const content = '<html><head><title>SOSI-kontroll</title>' +
            style +
            '</head><body><div>' +
            data +
            '</div></body></html>';
        mywindow.document.write(content);
        angular.element(mywindow.document).ready(function() {
            mywindow.document.close(); // necessary for IE >= 10
            mywindow.focus(); // necessary for IE >= 10
            setTimeout(function() { // some times the result is lots of blank pages it we do not wait.
                mywindow.print();
                mywindow.close();
            }, 400);
        });

        return true;
    }

    function updateFileDestination() {
        if (contract.selectedFileDestination === 'NVDB') {
            contract.fileList = contract.nvdbFileList;
        } else {
            contract.fileList = contract.fkbFileList;
        }
        clearFileSelection();
    }

    function clear() {
        vm.filesNeedingOperation = {};
        vm.selectedFile = undefined;
        vm.selectedFiles.length = 0;
        vm.fileSelection.clear();
    }

    function clearFileSelection() {
        vm.selectedFile = undefined;
        vm.selectedFiles.length = 0;
        (vm.fileSelection as any).selected = [];
    }

    function pad(n: string, width: number, z = '0'): string {
        n = n + '';
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }

    function onSelectionChange(event: SelectionEvent<string>): void {
        vm.allOperations = null;
        vm.selectedFiles = event.newSelection.map(function(selectedFileId) {
            return contract.fileList.getFile(selectedFileId);
        });
        vm.selectedFiles.forEach(function(file: ContractFile) {

            vm.filesNeedingOperation[file.id] = {
                loading: false,
                wantedOperation: file.wantedOperation
            };

            if (file.size < vm.MAX_FILESIZE_FOR_DISPLAY) {
                contract.readFileContent(file).then((file: any) => {
                    angular.forEach(file.issues, function(issue) {
                        // alias can be PUNKT 28, PUNKT 2, PUNKT 3. Pad with zeroes to three digits to make sorting easier.
                        const alias = issue.location.featureAlias;
                        if (alias) {
                            const parts = alias.split(' ');
                            if (parts.length >= 2) {
                                issue.location.featureAliasPadded = parts[0] + pad(parts[1], 3);
                            } else {
                                issue.location.featureAliasPadded = alias;
                            }
                        }
                    });
                });
            }
        });

        if (event.newSelection.length === 1) {
            vm.selectedFile = vm.selectedFiles[0];
        } else {
            vm.selectedFile = undefined;
        }
    }

    function getNrOfFeatureRejectionIssues(fileId: string): number {
        return rejectedFeatureValidationIssues(fileId).length;
    }

    function getNrOfRejectedFeatureWithUniqueAlias(fileId: string): number {
        return rejectedFeatureValidationIssuesWithUniqueAlias(fileId).length;
    }

    function getIssueList(fileId: string): ValidationIssue[] {
        switch (vm.errorView) {
            case 'ALL':
                return vm.contract.fileList.files.filter(file => file.id === fileId)[0].issues;
            case 'ERRORS':
                return rejectedFeatureValidationIssues(fileId);
            case 'ERRORS-UNIQUE-ALIAS':
                return rejectedFeatureValidationIssuesWithUniqueAlias(fileId);
        }
    }

    function getNrOfFeatures(fileId: string): number {
        let count = 0;
        const featureList = isShowingNvdbFiles() ? vm.contract.featureList.features : vm.contract.fkbFeatureList.features;
        for (const featureType in featureList) {
            if (featureList.hasOwnProperty(featureType)) {
                featureList[featureType].forEach(feature => {
                    if (feature.fileId === fileId) {
                        count++;
                    }
                });
            }
        }
        return count;
    }

    function rejectedFeatureValidationIssues(fileId: string) {
        const filesList = isShowingNvdbFiles() ? vm.contract.nvdbFileList : vm.contract.fkbFileList;
        return filesList
            .files
            .filter(file => file.id === fileId)[0]
            .issues
            .filter(issue => issue.location.belongsToRejectedFeature);
    }

    function rejectedFeatureValidationIssuesWithUniqueAlias(fileId: string) {
        const rejectionIssues = rejectedFeatureValidationIssues(fileId);
        const issuesWithUniqueAlias: ValidationIssue[] = [];
        rejectionIssues.forEach(issue => {
            if (!issuesWithUniqueAlias.some(registered => registered.location.featureAlias === issue.location.featureAlias)) {
                issuesWithUniqueAlias.push(issue);
            }
        });
        return issuesWithUniqueAlias;
    }

    function isShowingNvdbFiles() {
        return contract.selectedFileDestination === 'NVDB';
    }
}

ContractFilesController.$inject = ['$q', '$uibModal', '$scope', '$rootScope', 'AUTH_EVENTS', '$routeParams', 'contract', 'security'];
